// src/hooks/useStripe.js - Hook replacement that uses StripeStore
import { useMemo } from 'react';
import useStripeStore from '../stores/StripeStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing Stripe data from StripeStore
 * 
 * Following the same pattern as useCustomers, useInvoices, etc.
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Stripe data and operations
 */
export const useStripe = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Main Stripe connection data
  const stripeConnection = useStripeStore(state => state.stripeConnection);
  const stripeConnectionLoading = useStripeStore(state => state.stripeConnectionLoading);

  // Setup flow state
  const setupInProgress = useStripeStore(state => state.setupInProgress);
  const onboardingUrl = useStripeStore(state => state.onboardingUrl);
  
  // Dashboard and management state
  const dashboardUrl = useStripeStore(state => state.dashboardUrl);
  const generatingDashboardLink = useStripeStore(state => state.generatingDashboardLink);
  
  // Action states
  const disconnecting = useStripeStore(state => state.disconnecting);
  const refreshingStatus = useStripeStore(state => state.refreshingStatus);
  
  // Connection state
  const isConnected = useStripeStore(state => state.isConnected);
  
  // Socket operations (functions don't change, so we can get them once)
  const loadStripeConnectionStatus = useStripeStore(state => state.loadStripeConnectionStatus);
  const startExpressSetup = useStripeStore(state => state.startExpressSetup);
  const createOnboardingLink = useStripeStore(state => state.createOnboardingLink);
  const checkOnboardingStatus = useStripeStore(state => state.checkOnboardingStatus);
  const refreshStripeCapabilities = useStripeStore(state => state.refreshStripeCapabilities);
  const getExpressDashboardLink = useStripeStore(state => state.getExpressDashboardLink);
  const disconnectStripeAccount = useStripeStore(state => state.disconnectStripeAccount);
  const clearDashboardUrl = useStripeStore(state => state.clearDashboardUrl);

  // Utility functions
  const getConnectionStatus = useStripeStore(state => state.getConnectionStatus);
  const canProcessPayments = useStripeStore(state => state.canProcessPayments);
  const requiresSetup = useStripeStore(state => state.requiresSetup);
  const getStatusMessage = useStripeStore(state => state.getStatusMessage);
  const getNextAction = useStripeStore(state => state.getNextAction);
  
  // Internal initialization state (for fallback check)
  const _initialized = useStripeStore(state => state._initialized);
  
  // Fallback initialization check (safety net - following useCustomers pattern)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useStripe: Store not initialized at app level, trying fallback');
    socket.initializeStore('StripeStore');
  }
  
  // FIXED: Computed values (memoized like other hooks)
  const currentStatus = useMemo(() => {
    return getConnectionStatus();
  }, [getConnectionStatus]);
  
  const currentStatusMessage = useMemo(() => {
    return getStatusMessage();
  }, [getStatusMessage]);
  
  const currentNextAction = useMemo(() => {
    return getNextAction();
  }, [getNextAction]);
  
  const currentCanAcceptPayments = useMemo(() => {
    return canProcessPayments();
  }, [canProcessPayments]);
  
  const currentNeedsSetup = useMemo(() => {
    return requiresSetup();
  }, [requiresSetup]);
  
  // Return the same interface pattern as other hooks
  return {
    // Main Stripe connection data
    stripeConnection,
    stripeConnectionLoading,

    // Setup flow state
    setupInProgress,
    onboardingUrl,

    // Dashboard and management state
    dashboardUrl,
    generatingDashboardLink,

    // Action states
    disconnecting,
    refreshingStatus,

    // Socket operations
    loadStripeConnectionStatus,
    startExpressSetup,
    createOnboardingLink,
    checkOnboardingStatus,
    refreshStripeCapabilities,
    getExpressDashboardLink,
    disconnectStripeAccount,
    clearDashboardUrl,

    // Utility functions
    getConnectionStatus,
    canProcessPayments,
    requiresSetup,
    getStatusMessage,
    getNextAction,

    // Connection state (use socket auth state like other hooks)
    isConnected: socket.isAuthenticated,

    // Legacy aliases for backward compatibility if needed
    loading: stripeConnectionLoading,

    // FIXED: Memoized computed values that only update when dependencies change
    status: currentStatus,
    statusMessage: currentStatusMessage,
    nextAction: currentNextAction,
    canAcceptPayments: currentCanAcceptPayments,
    needsSetup: currentNeedsSetup
  };
};

export default useStripe;